import { API_BASE_URL, ADMIN_API_KEY } from './msalConfig';

/**
 * API client for LinePe backend
 * Uses API key authentication (not Azure AD tokens)
 */
class ApiClient {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    this.apiKey = ADMIN_API_KEY;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add API key if available
    if (this.apiKey) {
      headers['x-api-key'] = this.apiKey;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      // Handle both string errors and object errors { code, message }
      const errorMsg = typeof errorData.error === 'string' 
        ? errorData.error 
        : errorData.error?.message || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return response.json();
  }

  // Health check
  async health() {
    return this.request('/api/health');
  }

  // Get all prompts
  async getPrompts() {
    return this.request('/api/prompts');
  }

  // Get prompt pairs
  async getPromptPairs() {
    return this.request('/api/prompt-pairs');
  }

  // Get API keys
  async getApiKeys() {
    return this.request('/api/admin/keys');
  }

  // Get leaderboard - fetches users with reward points from SQL Server
  // Uses stored procedure: USP_Admin_Invoice_TopUsersByPoints
  async getLeaderboard(params?: { limit?: number; top?: number }) {
    // Backend uses 'top' parameter, map 'limit' to 'top' for convenience
    const queryParams: Record<string, string> = {};
    if (params?.top) queryParams.top = params.top.toString();
    else if (params?.limit) queryParams.top = params.limit.toString();
    const query = new URLSearchParams(queryParams).toString();
    return this.request(`/api/admin/leaderboard${query ? `?${query}` : ''}`);
  }

  // Get user details
  async getUserDetails(userId: string) {
    return this.request(`/api/admin/user/${userId}`);
  }

  // Generate card
  async generateCard(data: any) {
    return this.request('/api/render', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get Azure AD configuration
  async getAzureConfig() {
    return this.request('/api/admin/auth/config');
  }

  // Documentation endpoints
  async getDocsList() {
    return this.request('/api/admin/docs/list');
  }

  async getDoc(docId: string) {
    return this.request(`/api/admin/docs/${docId}`);
  }

  // API Endpoints (structured)
  async getAllEndpoints() {
    return this.request('/api/admin/endpoints/all');
  }

  async getEndpoint(endpointId: string) {
    return this.request(`/api/admin/endpoints/${endpointId}`);
  }

  // API Key Management
  async listApiKeys() {
    return this.request('/api/admin/keys');
  }

  async generateApiKey(data: { tier: string; name: string; rateLimit?: number }) {
    return this.request('/api/admin/keys/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(keyPrefix: string) {
    return this.request('/api/admin/keys/revoke', {
      method: 'POST',
      body: JSON.stringify({ keyPrefix }),
    });
  }

  async enableApiKey(keyPrefix: string) {
    return this.request('/api/admin/keys/enable', {
      method: 'POST',
      body: JSON.stringify({ keyPrefix }),
    });
  }

  async revealApiKey(keyPrefix: string) {
    return this.request('/api/admin/keys/reveal', {
      method: 'POST',
      body: JSON.stringify({ keyPrefix }),
    });
  }

  // Content Manager endpoints
  async getAllContent(params?: { limit?: number; continuationToken?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/admin/content/all${query ? `?${query}` : ''}`);
  }

  async getContent(id: string, userId: string) {
    return this.request(`/api/admin/content/${id}?userId=${userId}`);
  }

  async updateContent(id: string, data: any) {
    return this.request(`/api/admin/content/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateContent(items: Array<{ id: string; userId: string }>, updates: any) {
    return this.request('/api/admin/content/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ items, updates }),
    });
  }

  async getContentPreview(id: string, userId: string) {
    return this.request(`/api/admin/content/${id}/preview?userId=${userId}`);
  }

  // ============================================================================
  // POINTS SYSTEM API
  // ============================================================================

  // Points Health
  async pointsHealth() {
    return this.request('/api/v1/points/health');
  }

  // Points Dashboard
  async getPointsDashboard() {
    return this.request('/api/admin/points/audit/dashboard');
  }

  // Points Activity
  async getPointsActivity(period: 'today' | 'week' | 'month') {
    return this.request(`/api/admin/points/audit/activity/${period}`);
  }

  // Points Audit Logs
  async getPointsAuditLogs(params?: { action?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/admin/points/audit/logs${query ? `?${query}` : ''}`);
  }

  // Points Configuration
  async getPointsConfig(configType: string) {
    return this.request(`/api/admin/points/config/${configType}`);
  }

  async updatePointsConfig(configType: string, data: any) {
    return this.request(`/api/admin/points/config/${configType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Points Users
  async searchPointsUsers(search?: string, limit?: number) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit) params.append('limit', limit.toString());
    return this.request(`/api/admin/points/users?${params.toString()}`);
  }

  async getPointsUserDetails(userId: string) {
    return this.request(`/api/admin/points/users/${userId}`);
  }

  async adjustUserPoints(userId: string, data: { points: number; reason: string; type: 'credit' | 'debit' }) {
    return this.request(`/api/admin/points/users/${userId}/adjust`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Points Catalog
  async getPointsCategories() {
    return this.request('/api/admin/points/catalog/categories');
  }

  async getPointsOperators(categoryId?: string) {
    const query = categoryId ? `?categoryId=${categoryId}` : '';
    return this.request(`/api/admin/points/catalog/operators${query}`);
  }

  // Points Redemption Orders
  async getRedemptionOrders(params?: { status?: string; limit?: number }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/admin/points/redemption/orders${query ? `?${query}` : ''}`);
  }

  async completeRedemptionOrder(orderId: string) {
    return this.request(`/api/admin/points/redemption/orders/${orderId}/complete`, {
      method: 'POST',
    });
  }

  // Points Migration
  async getMigrationStats() {
    return this.request('/api/admin/points/migration/stats');
  }

  async getMigrationBatches() {
    return this.request('/api/admin/points/migration/batches');
  }

  async getMigrationBatchDetails(batchId: string) {
    return this.request(`/api/admin/points/migration/batches/${batchId}?includeTransactions=true`);
  }

  // Points Expiry
  async getExpiryStats() {
    return this.request('/api/admin/points/expiry/stats');
  }

  async runExpiryJob() {
    return this.request('/api/admin/points/expiry/run', {
      method: 'POST',
    });
  }

  async getExpiryPreview() {
    return this.request('/api/admin/points/expiry/preview');
  }

  async getUsersWithExpiringPoints() {
    return this.request('/api/admin/points/expiry/users');
  }

  // Points Catalog Admin
  async createCategory(data: any) {
    return this.request('/api/admin/points/catalog/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(categoryId: string, data: any) {
    return this.request(`/api/admin/points/catalog/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createOperator(data: any) {
    return this.request('/api/admin/points/catalog/operators', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOperator(operatorId: string, data: any) {
    return this.request(`/api/admin/points/catalog/operators/${operatorId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async syncOperatorPlans(operatorId: string) {
    return this.request(`/api/admin/points/catalog/operators/${operatorId}/sync-plans`, {
      method: 'POST',
    });
  }

  // Migration Admin
  async createMigrationBatch(data: { name: string; description?: string; userFilter?: any }) {
    return this.request('/api/admin/points/migration/batches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async executeMigrationBatch(batchId: string, users: Array<{ userId: string; legacyPoints: number }>) {
    return this.request(`/api/admin/points/migration/batches/${batchId}/execute`, {
      method: 'POST',
      body: JSON.stringify({ users }),
    });
  }

  async rollbackMigrationBatch(batchId: string) {
    return this.request(`/api/admin/points/migration/batches/${batchId}/rollback`, {
      method: 'POST',
    });
  }

  async previewMigration(users: Array<{ userId: string; legacyPoints: number }>) {
    return this.request('/api/admin/points/migration/preview', {
      method: 'POST',
      body: JSON.stringify({ users }),
    });
  }

  // Notifications Admin
  async getNotificationEvents() {
    return this.request('/api/admin/points/notifications/events');
  }

  async getNotificationTemplates() {
    return this.request('/api/admin/points/notifications/templates');
  }

  async triggerNotification(userId: string, eventType: string) {
    return this.request('/api/admin/points/notifications/trigger', {
      method: 'POST',
      body: JSON.stringify({ userId, eventType }),
    });
  }

  async sendTestNotification(userId: string, channel: string, message: string) {
    return this.request('/api/admin/points/notifications/test', {
      method: 'POST',
      body: JSON.stringify({ userId, channel, message }),
    });
  }

  // All Configurations
  async getAllPointsConfigs() {
    return Promise.all([
      this.getPointsConfig('earning'),
      this.getPointsConfig('expiry'),
      this.getPointsConfig('redemption'),
      this.getPointsConfig('payment_gateway'),
      this.getPointsConfig('migration'),
      this.getPointsConfig('notifications'),
    ]);
  }

  // ============================================================================
  // BBPS Gateway APIs
  // ============================================================================

  async getBBPSConfig() {
    return this.request('/api/admin/points/bbps/config');
  }

  async getBBPSConfigSummary() {
    return this.request('/api/admin/points/bbps/config/summary');
  }

  async getBBPSProviders() {
    return this.request('/api/admin/points/bbps/providers');
  }

  async getBBPSHealth() {
    return this.request('/api/admin/points/bbps/health');
  }

  async getBBPSSyncStatus() {
    return this.request('/api/admin/points/bbps/catalog/sync/status');
  }

  async setActiveProvider(provider: string) {
    return this.request('/api/admin/points/bbps/config/active-provider', {
      method: 'PUT',
      body: JSON.stringify({ provider }),
    });
  }

  async setFallbackProvider(provider: string | null) {
    return this.request('/api/admin/points/bbps/config/fallback-provider', {
      method: 'PUT',
      body: JSON.stringify({ provider }),
    });
  }

  async initializeBBPSFactory() {
    return this.request('/api/admin/points/bbps/factory/initialize', {
      method: 'POST',
    });
  }

  async refreshBBPSFactory() {
    return this.request('/api/admin/points/bbps/factory/refresh', {
      method: 'POST',
    });
  }

  async triggerCatalogSync(options: { provider?: string; dryRun?: boolean } = {}) {
    return this.request('/api/admin/points/bbps/catalog/sync', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async previewCatalogSync(options: { provider?: string } = {}) {
    return this.request('/api/admin/points/bbps/catalog/sync/preview', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async startScheduledSync(intervalHours: number = 24) {
    return this.request('/api/admin/points/bbps/catalog/sync/schedule/start', {
      method: 'POST',
      body: JSON.stringify({ intervalHours }),
    });
  }

  async stopScheduledSync() {
    return this.request('/api/admin/points/bbps/catalog/sync/schedule/stop', {
      method: 'POST',
    });
  }

  async checkProviderHealth(provider: string) {
    return this.request('/api/admin/points/bbps/health/check', {
      method: 'POST',
      body: JSON.stringify({ provider }),
    });
  }

  async clearBBPSCache() {
    return this.request('/api/admin/points/bbps/cache', {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Phase 15: Health Monitoring APIs
  // ============================================================================

  async startHealthMonitor(config: { intervalMs?: number; enableAutoFailover?: boolean } = {}) {
    return this.request('/api/admin/points/bbps/health/start', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async stopHealthMonitor() {
    return this.request('/api/admin/points/bbps/health/stop', {
      method: 'POST',
    });
  }

  async triggerHealthCheck() {
    return this.request('/api/admin/points/bbps/health/check', {
      method: 'POST',
    });
  }

  async checkProviderHealthNow(provider: string) {
    return this.request(`/api/admin/points/bbps/health/check/${provider}`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // Phase 15: Circuit Breaker APIs
  // ============================================================================

  async getCircuitBreakerStatus() {
    return this.request('/api/admin/points/bbps/circuit-breaker');
  }

  async resetCircuitBreaker(provider: string) {
    return this.request(`/api/admin/points/bbps/circuit-breaker/reset/${provider}`, {
      method: 'POST',
    });
  }

  async resetAllCircuitBreakers() {
    return this.request('/api/admin/points/bbps/circuit-breaker/reset-all', {
      method: 'POST',
    });
  }

  // ============================================================================
  // Phase 15: Audit Log APIs
  // ============================================================================

  async queryAuditLogs(filters: {
    provider?: string;
    eventType?: string;
    startDate?: string;
    endDate?: string;
    userId?: string;
    orderId?: string;
    limit?: number;
  } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    const queryString = params.toString();
    return this.request(`/api/admin/points/bbps/audit${queryString ? '?' + queryString : ''}`);
  }

  async configureAuditLogger(config: {
    enabled?: boolean;
    logToConsole?: boolean;
    logToCosmos?: boolean;
    retentionDays?: number;
  }) {
    return this.request('/api/admin/points/bbps/audit/configure', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }
}

export const api = new ApiClient();

